/**
 * ScrapeKing Design System v2 - Alert Component
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

type AlertVariant = 'info' | 'success' | 'warning' | 'error';

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const variantStyles = {
  info: css`
    --alert-color: var(--sk-cyan-500);
    --alert-bg: rgba(0, 200, 224, 0.1);
    --alert-border: var(--sk-cyan-700);
    --alert-glow: var(--sk-cyan-glow);
  `,
  success: css`
    --alert-color: var(--sk-green-500);
    --alert-bg: rgba(0, 255, 127, 0.1);
    --alert-border: var(--sk-green-700);
    --alert-glow: var(--sk-green-glow);
  `,
  warning: css`
    --alert-color: var(--sk-amber-500);
    --alert-bg: rgba(255, 184, 0, 0.1);
    --alert-border: var(--sk-amber-700);
    --alert-glow: var(--sk-amber-glow);
  `,
  error: css`
    --alert-color: var(--sk-red-500);
    --alert-bg: rgba(255, 0, 51, 0.1);
    --alert-border: var(--sk-red-700);
    --alert-glow: var(--sk-red-glow);
  `,
};

const AlertWrapper = styled.div<{ $variant: AlertVariant }>`
  ${props => variantStyles[props.$variant]}

  display: flex;
  align-items: flex-start;
  gap: var(--sk-space-3);
  padding: var(--sk-space-4);
  background: var(--alert-bg);
  border: 1px solid var(--alert-border);
  border-radius: var(--sk-radius-default);
  animation: ${slideIn} 0.3s ease;
  position: relative;
  overflow: hidden;

  /* Left accent bar */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: var(--alert-color);
    box-shadow: 0 0 10px var(--alert-glow);
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  color: var(--alert-color);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-sm);
  font-weight: 600;
  color: var(--sk-text-primary);
  margin-bottom: var(--sk-space-1);
`;

const Message = styled.div`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
  line-height: 1.5;
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--sk-radius-sm);
  color: var(--sk-text-tertiary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: var(--sk-text-secondary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ═══════════════════════════════════════════════════════════════
// ICONS
// ═══════════════════════════════════════════════════════════════

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const variantIcons = {
  info: InfoIcon,
  success: SuccessIcon,
  warning: WarningIcon,
  error: ErrorIcon,
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: React.ReactNode;
  onClose?: () => void;
  icon?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const Alert: FC<AlertProps> = ({
  variant = 'info',
  title,
  children,
  onClose,
  icon,
  className,
  style,
}) => {
  const IconComponent = variantIcons[variant];

  return (
    <AlertWrapper $variant={variant} className={className} style={style}>
      <IconWrapper>
        {icon || <IconComponent />}
      </IconWrapper>
      <Content>
        {title && <Title>{title}</Title>}
        <Message>{children}</Message>
      </Content>
      {onClose && (
        <CloseButton onClick={onClose} aria-label="Close alert">
          <CloseIcon />
        </CloseButton>
      )}
    </AlertWrapper>
  );
};

export default Alert;
